import { useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { motion } from "framer-motion";
import { CircleNotch, EnvelopeSimple, MapPin, Phone, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "@/components/ui/sonner";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { SectionHeader } from "@/components/SectionHeader";
import { contact } from "@/data/site";

const enquirySchema = z.object({
  name: z.string().min(2, "Enter your full name"),
  company: z.string().optional(),
  email: z.string().email("Enter a valid email address"),
  phone: z.string().min(8, "Enter a valid phone number").regex(/^[0-9+\-\s()]+$/, "Use digits and phone symbols only"),
  service: z.enum(["manpower", "equipment", "transaction", "general"]),
  location: z.string().min(2, "Enter the project location"),
  message: z.string().min(10, "Tell us a little more about the requirement"),
});

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_VALUES = { name: "", company: "", email: "", phone: "", service: "equipment", location: "", message: "" };
const LABEL_CLASS = "font-mono text-xs uppercase tracking-[0.18em] text-industrial-muted";
const panelLeftInitial = { opacity: 0, x: -28 };
const panelRightInitial = { opacity: 0, x: 28 };
const panelInView = { opacity: 1, x: 0 };
const viewportOnce = { once: true };

const contactDetails = [
  [MapPin, "Registered Office", contact.address, "contact-address"],
  [Phone, "Phone", contact.phone, "contact-phone"],
  [EnvelopeSimple, "Email", contact.email, "contact-email"],
];

const TextField = ({ control, name, label, testId, placeholder, type = "text" }) => (
  <FormField control={control} name={name} render={({ field }) => (
    <FormItem>
      <FormLabel className={LABEL_CLASS}>{label}</FormLabel>
      <FormControl><input data-testid={testId} type={type} className="input-industrial" placeholder={placeholder} {...field} /></FormControl>
      <FormMessage />
    </FormItem>
  )} />
);

const ContactDetailsPanel = () => (
  <motion.div initial={panelLeftInitial} whileInView={panelInView} viewport={viewportOnce} className="grid content-start gap-px border border-industrial-line bg-industrial-line lg:col-span-2" data-testid="contact-details-panel">
    {contactDetails.map(([Icon, label, value, testId]) => (
      <div key={label} className="bg-industrial-surface p-6" data-testid={testId}>
        <Icon size={30} weight="fill" className="text-industrial-yellow" />
        <p className="mt-5 font-mono text-xs uppercase tracking-[0.22em] text-industrial-muted">{label}</p>
        <p className="mt-2 text-base font-medium leading-7 text-industrial-text">{value}</p>
      </div>
    ))}
    <div className="bg-industrial-yellow p-6 text-industrial-black" data-testid="contact-pan-india-panel">
      <p className="font-mono text-xs font-bold uppercase tracking-[0.22em]">Service Coverage</p>
      <p className="mt-2 font-heading text-3xl font-semibold uppercase">PAN India</p>
    </div>
  </motion.div>
);

const FormActions = ({ isSubmitting }) => (
  <div className="flex flex-col gap-4 sm:flex-row">
    <button type="submit" disabled={isSubmitting} data-testid="enquiry-submit-button" className="inline-flex h-14 flex-1 items-center justify-center gap-3 bg-industrial-yellow px-8 font-mono text-xs font-bold uppercase tracking-[0.22em] text-industrial-black transition-all hover:-translate-y-1 hover:hard-shadow-sm disabled:cursor-not-allowed disabled:opacity-60">
      {isSubmitting && <CircleNotch size={18} className="animate-spin" weight="bold" />} Send Enquiry
    </button>
    <a href={`https://wa.me/${contact.whatsapp}?text=${encodeURIComponent(contact.whatsappMessage)}`} target="_blank" rel="noopener noreferrer" data-testid="enquiry-whatsapp-button" className="inline-flex h-14 items-center justify-center gap-3 border border-[#1E9E52] px-8 font-mono text-xs font-bold uppercase tracking-[0.22em] text-[#157A3E] transition-all hover:-translate-y-1 hover:bg-[#1E9E52] hover:text-white">
      <WhatsappLogo size={20} weight="fill" /> WhatsApp Us
    </a>
  </div>
);

const EnquiryForm = () => {
  const [status, setStatus] = useState("");
  const form = useForm({ resolver: zodResolver(enquirySchema), defaultValues: DEFAULT_VALUES });

  const onSubmit = async (values) => {
    setStatus("");
    try {
      const response = await axios.post(`${API}/enquiries`, values);
      const reference = response.data?.id ? ` Reference ${response.data.id.slice(0, 8).toUpperCase()}` : "";
      setStatus(`Enquiry received.${reference}`);
      toast.success("Enquiry sent successfully", { description: "Shyam Equipment and Infratech will contact you shortly." });
      form.reset(DEFAULT_VALUES);
    } catch (error) {
      const message = error.response?.data?.detail || "Please try again or call us directly.";
      toast.error("Could not send enquiry", { description: message });
      setStatus("Submission failed. Please try again.");
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-5" data-testid="enquiry-form">
        <div className="grid gap-5 sm:grid-cols-2">
          <TextField control={form.control} name="name" label="Full name" testId="enquiry-name-input" placeholder="Your name" />
          <TextField control={form.control} name="company" label="Company" testId="enquiry-company-input" placeholder="Organization" />
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <TextField control={form.control} name="email" label="Email" testId="enquiry-email-input" placeholder="name@company.com" type="email" />
          <TextField control={form.control} name="phone" label="Phone" testId="enquiry-phone-input" placeholder="+91" />
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <FormField control={form.control} name="service" render={({ field }) => (
            <FormItem>
              <FormLabel className={LABEL_CLASS}>Requirement type</FormLabel>
              <FormControl>
                <select data-testid="enquiry-service-select" className="input-industrial" {...field}>
                  <option value="equipment">Equipment Rental</option>
                  <option value="manpower">Manpower Supply</option>
                  <option value="transaction">Transaction Support</option>
                  <option value="general">General Enquiry</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )} />
          <TextField control={form.control} name="location" label="Project location" testId="enquiry-location-input" placeholder="City / site location" />
        </div>
        <FormField control={form.control} name="message" render={({ field }) => (
          <FormItem>
            <FormLabel className={LABEL_CLASS}>Project requirement</FormLabel>
            <FormControl><textarea data-testid="enquiry-message-input" rows={6} className="input-industrial h-auto py-4" placeholder="Location, timeline, equipment or manpower required" {...field} /></FormControl>
            <FormMessage />
          </FormItem>
        )} />
        <FormActions isSubmitting={form.formState.isSubmitting} />
        {status && <p className="border border-industrial-line p-4 font-mono text-xs uppercase tracking-[0.16em] text-industrial-yellow" data-testid="enquiry-status-message">{status}</p>}
      </form>
    </Form>
  );
};

export function Contact() {
  return (
    <section id="contact" className="py-24 sm:py-32" data-testid="contact-section">
      <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
        <SectionHeader eyebrow="Contact / 08" title="Mobilize your next requirement" description="Share the project scope and our team will respond with practical manpower, equipment, or transaction support options." testId="contact-section-header" />
        <div className="grid gap-8 lg:grid-cols-5">
          <ContactDetailsPanel />
          <motion.div initial={panelRightInitial} whileInView={panelInView} viewport={viewportOnce} className="border border-industrial-line bg-industrial-surface p-6 sm:p-8 lg:col-span-3" data-testid="enquiry-form-panel">
            <EnquiryForm />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
